import { useState, useEffect, useRef } from "react"
import dayjs from "dayjs"
import PropTypes from "prop-types"
import isSameOrBefore from "dayjs/plugin/isSameOrBefore"
import isSameOrAfter from "dayjs/plugin/isSameOrAfter"
import isBetween from "dayjs/plugin/isBetween"

// components
import WorkflowTrackerState from "./subcomponents/WorkflowTrackerState/WorkflowTrackerState"
import { Tooltip } from "antd"

// utils
import { formatTrackerData, handleTrackerScroll } from "./utils"
import useWorkflowTracker from "../../../context/hooks/useWorkflowTracker"

// styles
import "./Tracker.scss"

dayjs.extend(isSameOrBefore)
dayjs.extend(isSameOrAfter)
dayjs.extend(isBetween)

const Tracker = ({
  model,
  workflowTrackerData = [],
  viewSettings,
  putWorkflowTracker,
  readOnly,
  isWorkflowComplete,
  formatPutReq,
  invalidateParentQueries,
  isEditStepModalOpenInOverview,
}) => {
  const { getDifferenceBetweenDays } = useWorkflowTracker()
  const { showAssignees, showDueDates, showDays } = viewSettings
  const [tempTrackerData, setTempTrackerData] = useState([])
  const [marginTop, setMarginTop] = useState(70)

  const pizzaTrackerRef = useRef()
  const wrapRef1 = useRef(null)
  const wrapRef2 = useRef(null)

  useEffect(() => {
    if (!showDueDates || readOnly) {
      setMarginTop(70)
    } else {
      setMarginTop(190)
    }
  }, [showDueDates])

  useEffect(() => {
    setTempTrackerData(formatTrackerData(workflowTrackerData))
  }, [workflowTrackerData, isWorkflowComplete])

  const onSaveStateDates = (ghostState) => {
    let tempData = [...tempTrackerData]
    let foundIndex = tempData.findIndex((item) => item.state_id === ghostState.state_id)
    let currState = tempData[foundIndex]

    const addDays = (date, days) => {
      return dayjs(date).add(days, "day").format("YYYY-MM-DD")
    }

    let ghostStateStartDate = dayjs(ghostState.start_state_date)
    let currStateStartDate = dayjs(currState.start_state_date)

    if (ghostStateStartDate.isAfter(currStateStartDate, "day")) {
      // date moved forward
      let prevState = tempData[foundIndex - 1]

      if (prevState) {
        prevState.end_state_date = ghostState.start_state_date
        prevState.goal_days = Math.abs(getDifferenceBetweenDays(prevState.end_state_date, prevState.start_state_date))
      }

      currState.start_state_date = ghostState.start_state_date
      let goal_days = getDifferenceBetweenDays(currState.end_state_date, currState.start_state_date)

      if (goal_days < 0) {
        goal_days = 0
        currState.end_state_date = currState.start_state_date
        currState.goal_days = 0
      } else {
        currState.goal_days = goal_days
      }

      for (let i = foundIndex + 1; i < tempData.length; i++) {
        let nextState = tempData[i]
        nextState.start_state_date = tempData[i - 1].end_state_date

        let checkDays = getDifferenceBetweenDays(nextState.end_state_date, nextState.start_state_date)

        if (checkDays < 0) {
          nextState.goal_days = 0
          nextState.end_state_date = nextState.start_state_date
        } else {
          nextState.goalDays = checkDays
        }
      }
    } else {
      // date moved back

      currState.start_state_date = ghostState.start_state_date
      currState.goal_days = getDifferenceBetweenDays(currState.start_state_date, currState.end_state_date)

      if (currState.goal_days < 0) {
        currState.goal_days = 0
      }
      currState.end_state_date = addDays(currState.start_state_date, currState.goal_days)

      if (!currState.end_state_date) {
        currState.end_state_date = addDays(currState.start_state_date, currState.goal_days)
      }

      for (let i = foundIndex - 1; i >= 0; i--) {
        let prevState = tempData[i]
        let currState = tempData[i + 1]
        let goal_days = getDifferenceBetweenDays(currState.start_state_date, prevState.start_state_date)

        if (goal_days > 0) {
          prevState.end_state_date = currState.start_state_date
          prevState.goal_days = goal_days
        } else {
          prevState.goal_days = 0
          prevState.start_state_date = currState.start_state_date
          prevState.end_state_date = currState.start_state_date
        }
      }
    }

    let request = formatPutReq({ stateData: tempData, model: model, workflowTrackerData: workflowTrackerData })

    putWorkflowTracker({
      body: request,
    })
  }

  const onRevertStateDates = () => {
    setTempTrackerData(workflowTrackerData?.state_data?.tracker_data.slice(1))
  }

  return (
    <div className="pizza-tracker-wrapper">
      {tempTrackerData?.length > 8 && (
        <div id={`#wrapper-1-container`}>
          <div
            ref={wrapRef1}
            onScroll={(e) => handleTrackerScroll(e, wrapRef1, wrapRef2)}
            id="main-tracker-scroll-container" //main-tracker-scroll-container
          >
            <div
              className="div1"
              id="pizza-tracker-remote-scroll"
            ></div>
          </div>
          <Tooltip
            placement="bottom"
            title={"Scroll to view all states"}
          >
            <i className="fal fa-info-circle remote-scroll-info-icon"></i>
          </Tooltip>
        </div>
      )}
      <div
        id="remote-tracker-scroll" // remote-tracker-scroll
        ref={wrapRef2}
        onScroll={(e) => handleTrackerScroll(e, wrapRef1, wrapRef2)}
      >
        <div
          className={`pizza-tracker ${showDueDates === false ? " due-dates-shown" : "show-milestone-dates"}`}
          ref={pizzaTrackerRef}
        >
          {tempTrackerData?.map((state, stateIndex) => {
            return (
              <WorkflowTrackerState
                readOnly={readOnly}
                key={stateIndex + "-state"}
                pizzaTrackerRef={pizzaTrackerRef}
                tempTrackerData={tempTrackerData}
                setTempTrackerData={setTempTrackerData}
                index={stateIndex}
                isFirst={stateIndex === 0}
                isLast={tempTrackerData?.length === stateIndex + 1}
                isToday={state.isToday}
                state={state}
                onSaveStateDates={onSaveStateDates}
                onRevertStateDates={onRevertStateDates}
                showAssignees={showAssignees}
                showDueDates={showDueDates}
                showDays={showDays}
                trackerMarginTop={marginTop}
                setTrackerMarginTop={setMarginTop}
                milestones={state?.milestones || []}
                invalidateParentQueries={invalidateParentQueries}
                isEditStepModalOpenInOverview={isEditStepModalOpenInOverview}
              />
            )
          })}
        </div>
      </div>
    </div>
  )
}

Tracker.propTypes = {
  model: PropTypes.oneOf(["activity", "request"]),
  workflowTrackerData: PropTypes.shape({
    parent_data: PropTypes.shape({
      actual_completion_date: PropTypes.string,
      activity_due_date: PropTypes.string,
    }),
    state_data: PropTypes.shape({
      tracker_data: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }),
  viewSettings: PropTypes.shape({
    showAssignees: PropTypes.bool,
    showDueDates: PropTypes.bool,
    showDays: PropTypes.bool,
  }),
  putWorkflowTracker: PropTypes.func,
  readOnly: PropTypes.bool,
}

export default Tracker
